import {
  Component,
  OnInit,
  HostListener
} from '@angular/core';

import { AngularFirestore } from '@angular/fire/firestore';

import { EnglishCourse } from 'src/app/models/english-course';
import { ActivatedRoute, Router } from '@angular/router';
import { EnglishLesson } from 'src/app/models/english-lesson';

import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder
} from '@angular/forms';

import { Observable } from 'rxjs';

import { Level } from 'src/app/models/level';

import {
  Translate,
  getLocalTranslate
} from 'src/app/helpers/global-functions';
import { Title } from '@angular/platform-browser';

import { SharedService } from 'src/app/services/shared.service';

const defaultLevel: Level = {
  id: '',
  titleEn: 'Choose Level',
  titleKh: 'Choose Level'
};

@Component({
  selector: 'app-english-courses',
  templateUrl: './english-courses.component.html',
  styleUrls: ['./english-courses.component.css']
})
export class EnglishCoursesComponent implements OnInit {

  // =========================================================
  // DATA
  // =========================================================

  courses: EnglishCourse[] = [];

  lessons: EnglishLesson[] = [];

  levels: Level[] = [];

  currentLessonId: string = '';

  currentLesson: EnglishLesson = new EnglishLesson();

  currentLevel: Level = new Level();

  isNoResult: boolean = false;

  defaultLesson: EnglishLesson = {
    id: '',
    nameEn: 'Choose Lesson',
    nameKh: 'ជ្រើសរើសមេរៀន',
    levels: []
  };


  // =========================================================
  // TRANSLATION
  // =========================================================

  searchHere: string = 'Search here...';

  home: string = 'Home';

  lessonsText: string = 'Lessons';


  // =========================================================
  // PAGINATION
  // =========================================================

  pageSize: number = 12;

  lastDoc: any = null;

  loadingMore: boolean = false;

  hasNextPage: boolean = true;


  // =========================================================
  // SEARCH
  // =========================================================

  allCourses: EnglishCourse[] = [];

  allCoursesLoaded: boolean = false;

  isSearching: boolean = false;

  loadingAllCourses: boolean = false;


  // =========================================================
  // OTHER
  // =========================================================

  firstLoad: boolean = true;

  showSpinner: boolean = false;

  searchForm: FormGroup;


  // =========================================================
  // CONSTRUCTOR
  // =========================================================

  constructor(
    private firestore: AngularFirestore,
    private route: ActivatedRoute,
    private router: Router,
    private formBuiler: FormBuilder,
    private sharedService: SharedService,
    private titleService: Title
  ) {}


  // =========================================================
  // INIT
  // =========================================================

  ngOnInit(): void {

    // -------------------------------------------------------
    // Banner
    // -------------------------------------------------------

    const id = 'UVWkDe2DcWtOKuAaWfi5';

    this.sharedService.getBanner(id).then(result => {

      if (
        result &&
        result.image &&
        result.image.fileUrl
      ) {

        const imageUrl = result.image.fileUrl;

        const bgCover =
          document.getElementById('bg-cover');

        if (bgCover) {

          bgCover.style.backgroundImage =
            'url(' + imageUrl + ')';

        }

      }

    });


    // -------------------------------------------------------
    // Initial setup
    // -------------------------------------------------------

    this.initialForm();

    this.getCourses();

    this.getLessons();

    this.needTranslate();

  }


  // =========================================================
  // TRANSLATION
  // =========================================================

  needTranslate(): void {

    this.searchHere =
      getLocalTranslate(
        'search-here',
        this.searchHere
      );

    this.home =
      getLocalTranslate(
        'home',
        this.home
      );

    this.lessonsText =
      getLocalTranslate(
        'english-lesson',
        this.lessonsText
      );

    this.titleService.setTitle(`${getLocalTranslate('english-lesson',this.lessonsText)} | CamEdu`)

  }


  // =========================================================
  // FORM
  // =========================================================

  initialForm(): void {

    this.searchForm =
      this.formBuiler.group({

        searchText: new FormControl('', [
          Validators.required
        ])

      });

  }


  // =========================================================
  // GET COURSES
  // =========================================================

  getCourses(): void {

    this.route.queryParams.subscribe(params => {

      const levelId =
        params.levelId || '';

      const lessonId =
        params.lessonId || '';

      const searchText =
        params.searchText || '';


      console.log(
        'Course params:',
        params
      );


      // -----------------------------------------------------
      // Current lesson ID
      // -----------------------------------------------------

      this.currentLessonId =
        lessonId;


      // -----------------------------------------------------
      // Reset
      // -----------------------------------------------------

      this.resetCourses();


      // -----------------------------------------------------
      // Update search form
      // -----------------------------------------------------

      this.searchForm.patchValue({
        searchText: searchText
      });


      // -----------------------------------------------------
      // Search
      // -----------------------------------------------------

      if (
        searchText.trim() !== ''
      ) {

        this.isSearching = true;

        this.searchAllCourses(
          searchText,
          levelId,
          lessonId
        );

        return;

      }


      // -----------------------------------------------------
      // Normal pagination
      // -----------------------------------------------------

      this.isSearching = false;

      this.loadMoreCourses(
        '',
        levelId,
        lessonId
      );

    });

  }


  // =========================================================
  // RESET
  // =========================================================

  resetCourses(): void {

    this.courses = [];

    this.allCourses = [];

    this.allCoursesLoaded = false;

    this.lastDoc = null;

    this.hasNextPage = true;

    this.loadingMore = false;

    this.loadingAllCourses = false;

    this.showSpinner = false;

    this.isNoResult = false;

  }


  // =========================================================
  // LOAD MORE COURSES
  // =========================================================

  loadMoreCourses(
    searchText: string = '',
    levelId?: string,
    lessonId?: string
  ): void {

    // -------------------------------------------------------
    // Do not paginate during search
    // -------------------------------------------------------

    if (this.isSearching) {
      return;
    }


    // -------------------------------------------------------
    // Prevent duplicate request
    // -------------------------------------------------------

    if (
      this.loadingMore ||
      !this.hasNextPage
    ) {
      return;
    }


    this.loadingMore = true;

    this.showSpinner = true;


    // -------------------------------------------------------
    // Firestore query
    // -------------------------------------------------------

    const query =
      this.queryCourses(
        levelId,
        lessonId,
        this.lastDoc
      );


    query.subscribe(

      queriedItems => {

        let docs =
          queriedItems.docs;


        // ---------------------------------------------------
        // Check next page
        // ---------------------------------------------------

        this.hasNextPage =
          docs.length > this.pageSize;


        // ---------------------------------------------------
        // Remove extra document
        // ---------------------------------------------------

        if (this.hasNextPage) {

          docs =
            docs.slice(
              0,
              this.pageSize
            );

        }


        // ---------------------------------------------------
        // Save cursor
        // ---------------------------------------------------

        if (docs.length > 0) {

          this.lastDoc =
            docs[docs.length - 1];

        }


        // ---------------------------------------------------
        // Convert documents
        // ---------------------------------------------------

        const newCourses =
          docs.map((doc: any) => {

            const course =
              new EnglishCourse();

            course.id =
              doc.id;

            Object.assign(
              course,
              doc.data()
            );

            return course;

          });


        // ---------------------------------------------------
        // Add courses
        // ---------------------------------------------------

        this.courses = [
          ...this.courses,
          ...newCourses
        ];


        // ---------------------------------------------------
        // Result
        // ---------------------------------------------------

        this.isNoResult =
          this.courses.length === 0;


        // ---------------------------------------------------
        // Finish loading
        // ---------------------------------------------------

        this.loadingMore = false;

        this.showSpinner = false;

        this.firstLoad = false;


        console.log(
          'Loaded courses:',
          newCourses.length
        );

        console.log(
          'Total courses:',
          this.courses.length
        );

        console.log(
          'Has next:',
          this.hasNextPage
        );

      },

      error => {

        this.loadingMore = false;

        this.showSpinner = false;

        console.error(
          'Firestore course error:',
          error
        );

      }

    );

  }


  // =========================================================
  // SEARCH ALL COURSES
  // =========================================================

  searchAllCourses(
    searchText: string,
    levelId?: string,
    lessonId?: string
  ): void {

    this.loadingAllCourses = true;

    this.showSpinner = true;

    this.isNoResult = false;


    // -------------------------------------------------------
    // Firestore query
    // -------------------------------------------------------

    const query =
      this.firestore.collection(
        'english-courses',

        ref => {

          let firestoreQuery: any =
            ref.where(
              'status',
              '==',
              1
            );


          // -------------------------------------------------
          // Lesson filter
          // -------------------------------------------------

          if (
            lessonId !== undefined &&
            lessonId !== ''
          ) {

            firestoreQuery =
              firestoreQuery.where(
                'lessonId',
                '==',
                lessonId
              );

          }


          // -------------------------------------------------
          // Level filter
          // -------------------------------------------------

          if (
            levelId !== undefined &&
            levelId !== ''
          ) {

            firestoreQuery =
              firestoreQuery.where(
                'levelId',
                '==',
                levelId
              );

          }


          // -------------------------------------------------
          // Order by updatedAt DESC
          // -------------------------------------------------

          firestoreQuery =
            firestoreQuery.orderBy(
              'updatedAt',
              'desc'
            );


          return firestoreQuery;

        }

      ).get();


    // -------------------------------------------------------
    // Result
    // -------------------------------------------------------

    query.subscribe(

      queriedItems => {

        const search =
          (
            searchText || ''
          )
            .trim()
            .toLowerCase();


        // ---------------------------------------------------
        // Convert Firestore documents
        // ---------------------------------------------------

        const allCourses =
          queriedItems.docs.map(
            (doc: any) => {

              const course =
                new EnglishCourse();

              course.id =
                doc.id;

              Object.assign(
                course,
                doc.data()
              );

              return course;

            }
          );


        // ---------------------------------------------------
        // Save all courses
        // ---------------------------------------------------

        this.allCourses =
          allCourses;

        this.allCoursesLoaded =
          true;


        // ---------------------------------------------------
        // Search ONLY nameEn + nameKh
        // ---------------------------------------------------

        this.courses =
          allCourses.filter(
            (course: any) => {

              const nameEn =
                (
                  course.nameEn ||
                  ''
                )
                  .toString()
                  .toLowerCase();


              const nameKh =
                (
                  course.nameKh ||
                  ''
                )
                  .toString()
                  .toLowerCase();


              return (
                nameEn.includes(search) ||
                nameKh.includes(search)
              );

            }
          );


        // ---------------------------------------------------
        // Result
        // ---------------------------------------------------

        this.isNoResult =
          this.courses.length === 0;


        // ---------------------------------------------------
        // Finish
        // ---------------------------------------------------

        this.loadingAllCourses = false;

        this.showSpinner = false;

        this.loadingMore = false;

        this.firstLoad = false;


        console.log(
          'Total matching courses:',
          this.courses.length
        );

      },

      error => {

        this.loadingAllCourses = false;

        this.showSpinner = false;

        this.loadingMore = false;

        console.error(
          'Search courses error:',
          error
        );

      }

    );

  }


  // =========================================================
  // FIRESTORE QUERY
  // =========================================================

  queryCourses(
    levelId?: string,
    lessonId?: string,
    cursor: any = null
  ): Observable<any> {

    return this.firestore.collection(
      'english-courses',

      ref => {

        let query: any =
          ref.where(
            'status',
            '==',
            1
          );

          // ---------------------------------------------------
          // Lesson filter
          // ---------------------------------------------------

          if (
            lessonId !== undefined &&
            lessonId !== ''
          ) {

            query =
              query.where(
                'lessonId',
                '==',
                lessonId
              );

          }

        // ---------------------------------------------------
        // Level filter
        // ---------------------------------------------------

        if (
          levelId !== undefined &&
          levelId !== ''
        ) {

          query =
            query.where(
              'levelId',
              '==',
              levelId
            );

        }


        // ---------------------------------------------------
        // Sort newest updated first
        // ---------------------------------------------------

        query =
          query.orderBy(
            'updatedAt',
            'desc'
          );


        // ---------------------------------------------------
        // Pagination cursor
        // ---------------------------------------------------

        if (cursor) {

          query =
            query.startAfter(
              cursor
            );

        }


        // ---------------------------------------------------
        // Get one extra document
        // ---------------------------------------------------

        query =
          query.limit(
            this.pageSize + 1
          );

        return query;

      }

    ).get();

  }


  // =========================================================
  // INFINITE SCROLL
  // =========================================================

  @HostListener('window:scroll', [])
  onWindowScroll(): void {

    // -------------------------------------------------------
    // Stop infinite scroll during search
    // -------------------------------------------------------

    if (this.isSearching) {
      return;
    }


    // -------------------------------------------------------
    // Prevent duplicate request
    // -------------------------------------------------------

    if (
      this.loadingMore ||
      !this.hasNextPage
    ) {
      return;
    }


    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;


    const windowHeight =
      window.innerHeight;


    const documentHeight =
      Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );


    const distanceFromBottom =
      documentHeight -
      (
        scrollTop +
        windowHeight
      );


    const threshold =
      window.innerWidth <= 768
        ? 1400
        : 600;


    if (
      distanceFromBottom <= threshold
    ) {

      const params =
        this.route.snapshot.queryParams;


      const levelId =
        params.levelId || '';


      const lessonId =
        params.lessonId || '';


      this.loadMoreCourses(
        '',
        levelId,
        lessonId
      );

    }

  }


  // =========================================================
  // GET LESSONS
  // =========================================================

  getLessons(): void {

    const query: Observable<any> =
      this.firestore.collection(
        'english-lessons',

        ref =>
          ref.where(
            'status',
            '==',
            1
          )

      ).get();


    query.subscribe(

      queriedItems => {

        const lessons =
          queriedItems.docs.map(
            (doc: any) => {

              const lesson =
                new EnglishLesson();


              // ------------------------------------------------
              // Set document ID first
              // ------------------------------------------------

              lesson.id =
                doc.id;


              // ------------------------------------------------
              // Copy Firestore data
              // ------------------------------------------------

              Object.assign(
                lesson,
                doc.data()
              );


              // ------------------------------------------------
              // Add default level AFTER Object.assign
              // ------------------------------------------------

              if (!lesson.levels) {

                lesson.levels = [];

              }


              lesson.levels = [
                defaultLevel,
                ...lesson.levels
              ];


              return lesson;

            }
          );


        // -----------------------------------------------------
        // Default level
        // -----------------------------------------------------

        this.currentLevel =
          defaultLevel;


        // -----------------------------------------------------
        // Lessons
        // -----------------------------------------------------

        this.lessons =
          lessons;


        this.lessons.unshift(
          this.defaultLesson
        );


        // -----------------------------------------------------
        // Current lesson
        // -----------------------------------------------------

        if (
          this.currentLessonId !== undefined &&
          this.currentLessonId !== ''
        ) {

          const filterLesson =
            this.lessons.filter(
              lesson =>
                lesson.id ===
                this.currentLessonId
            );


          if (
            filterLesson.length > 0
          ) {

            this.currentLesson =
              filterLesson[0];

          }

        } else {

          this.currentLesson =
            this.defaultLesson;

        }


        // -----------------------------------------------------
        // Set current level from URL
        // -----------------------------------------------------

        const params =
          this.route.snapshot.queryParams;


        const levelId =
          params.levelId || '';


        if (
          levelId !== '' &&
          this.currentLesson &&
          this.currentLesson.levels
        ) {

          const selectedLevel =
            this.currentLesson.levels.find(
              level =>
                level.id === levelId
            );


          if (selectedLevel) {

            this.currentLevel =
              selectedLevel;

          }

        }

      },

      error => {

        console.error(
          'Get lessons error:',
          error
        );

      }

    );

  }


  // =========================================================
  // COURSE DETAIL
  // =========================================================

  routeToCourseDetail(
    id: string
  ): void {

    this.router.navigate([
      '/lessons/' + id
    ]);

  }


  // =========================================================
  // LESSON CHANGE
  // =========================================================

  onChangeLesson(): void {

    const id =
      this.currentLesson.id;


    // Reset level
    this.currentLevel =
      defaultLevel;


    // -------------------------------------------------------
    // All lessons
    // -------------------------------------------------------

    if (id === '') {

      this.router.navigate([
        '/lessons'
      ]);

      return;

    }


    // -------------------------------------------------------
    // Selected lesson
    // -------------------------------------------------------

    this.router.navigate(
      ['/lessons'],
      {
        queryParams: {
          lessonId: id
        }
      }
    );

  }


  // =========================================================
  // LEVEL CHANGE
  // =========================================================

  onChangeLevel(): void {

    const id =
      this.currentLevel.id;


    const lessonId =
      this.currentLesson.id;


    // -------------------------------------------------------
    // Choose level
    // -------------------------------------------------------

    if (id === '') {

      if (lessonId === '') {

        this.router.navigate([
          '/lessons'
        ]);

      } else {

        this.router.navigate(
          ['/lessons'],
          {
            queryParams: {
              lessonId: lessonId
            }
          }
        );

      }

      return;

    }


    // -------------------------------------------------------
    // Selected level
    // -------------------------------------------------------

    this.router.navigate(
      ['/lessons'],
      {
        queryParams: {

          lessonId:
            lessonId,

          levelId:
            id

        }
      }
    );

  }


  // =========================================================
  // SEARCH
  // =========================================================

  onSubmit(): void {

    const searchText =
      (
        this.searchForm.value.searchText ||
        ''
      ).trim();


    const levelId =
      this.currentLevel.id;


    const lessonId =
      this.currentLesson.id;


    // -------------------------------------------------------
    // Empty search
    // -------------------------------------------------------

    if (searchText === '') {

      const queryParams: any = {};


      if (lessonId !== '') {

        queryParams.lessonId =
          lessonId;

      }


      if (levelId !== '') {

        queryParams.levelId =
          levelId;

      }


      this.router.navigate(
        ['/lessons'],
        {
          queryParams:
            queryParams
        }
      );

      return;

    }


    // -------------------------------------------------------
    // Search parameters
    // -------------------------------------------------------

    const queryParams: any = {

      searchText:
        searchText

    };


    if (lessonId !== '') {

      queryParams.lessonId =
        lessonId;

    }


    if (levelId !== '') {

      queryParams.levelId =
        levelId;

    }


    // -------------------------------------------------------
    // Navigate
    // -------------------------------------------------------

    this.router.navigate(
      ['/lessons'],
      {
        queryParams:
          queryParams
      }
    );

  }


  // =========================================================
  // TRANSLATE
  // =========================================================

  translate(
    textEn: string,
    textKh: string
  ): string {

    return Translate(
      textEn,
      textKh
    );

  }

}